import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Instagram, Share2 } from 'lucide-react';
import WhatsAppIcon from './icons/WhatsAppIcon';

interface SocialActionsProps {
  url: string;
  title: string;
}

const SocialActions: React.FC<SocialActionsProps> = ({ url, title }) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  useEffect(() => {
    // Re-parse Facebook social plugins when URL changes
    if ((window as any).FB) {
      (window as any).FB.XFBML.parse();
    }
  }, [url]);

  const shareWhatsApp = () => {
    window.open(`https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`, '_blank');
  };

  const shareInstagram = () => {
    // Instagram doesn't have a direct share URL for web to post.
    // Usually, we link to the app or a specific profile, or use a generic share.
    // For now, we'll use a helpful message or use the Web Share API if available.
    if (navigator.share) {
      navigator.share({
        title: title,
        url: url,
      }).catch(console.error);
    } else {
      // Fallback: Copy link and alert user
      navigator.clipboard.writeText(url);
      alert('Link copied! You can now share it on Instagram.');
    }
  };

  return (
    <div className="mt-12 pt-12 border-t border-slate-100 dark:border-white/10">
      <div className="flex flex-col gap-8">
        <div>
            <h3 className="text-xl font-semibold text-[#002a5c] dark:text-white mb-6 flex items-center gap-2">
              <Share2 size={24} className="text-blue-600" />
              Share & Interact
            </h3>
          
          <div className="flex flex-wrap gap-4 mb-8">
            {/* WhatsApp Share */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={shareWhatsApp}
              className="flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-xl font-semibold shadow-lg hover:shadow-[#25D366]/20 transition-all"
            >
              <WhatsAppIcon size={20} className="text-white" />
              WhatsApp
            </motion.button>

            {/* Instagram Share/Link */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={shareInstagram}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-tr from-[#f09433] via-[#e6683c] to-[#bc1888] text-white rounded-xl font-semibold shadow-lg hover:shadow-[#e6683c]/20 transition-all"
            >
              <Instagram size={20} />
              Instagram
            </motion.button>

            {/* FB Like Button */}
            <div className="flex items-center">
              <div 
                className="fb-like" 
                data-href={url} 
                data-width="" 
                data-layout="button_count" 
                data-action="like" 
                data-size="large" 
                data-share="true"
              ></div>
            </div>
          </div>
        </div>

        {/* FB Comments Plugin */}
        <div className="bg-white dark:bg-[#001a35] rounded-3xl p-6 shadow-sm border border-slate-100 dark:border-white/5">
            <h3 className="text-xl font-semibold text-[#002a5c] dark:text-white mb-6 flex items-center gap-2">
              <Share2 size={24} className="text-blue-600" />
              Comments
            </h3>
          <div className="fb-comments-container overflow-hidden rounded-xl bg-white p-4">
            <div 
              className="fb-comments" 
              data-href={url} 
              data-width="100%" 
              data-numposts="5"
              data-colorscheme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialActions;
